import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { ApplicationScreen, ScreenContextMenuItem } from 'src/app/application-navigator/application-screen';
import { MayerFabricsService } from 'src/app/services/mayer-fabrics.service';
import { AudioService, Sound } from 'src/app/services/audio.service';
import { StockItem } from 'src/app/acumatica/EntityServices/StockItem.service';
import { CollatedSetByItemService, KitAuditService } from 'src/app/acumatica/EntityServices/KitAudit.service';
import { KitAudit, KitAuditItem, KitAuditNotice_InpersistentMessage, KitAuditNotice_ItemInWrongKit, KitAuditSet, KitAuditType } from '../kit-audit';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialog } from 'src/app/common/confirmation-dialog/confirmation-dialog.component';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-audit-kit',
  templateUrl: './audit-kit.component.html',
  styleUrls: ['./audit-kit.component.css']
})
export class KitAuditAuditKit extends ApplicationScreen implements OnInit, OnDestroy {

  constructor(
    private router: Router,
    private location: Location,
    private dialog: MatDialog,
    private kitAudit: KitAuditService,
    private MayerFabrics: MayerFabricsService,
    private audio: AudioService,
    private collatedSetInquiry: CollatedSetByItemService,
    private snackbar: MatSnackBar
  ) {
    super();

    if(this.kitAudit.audit!.activeKit == undefined) {
      this.router.navigate(['kit-audit', 'select']);
    }

    this.pageTitle = 'Audit: ' + this.activeKit.Description;
    this.scannerSubscriber = this.onScannerInput.subscribe(value => this.receiveScannerValue(value));
  }
  
  screenMenuItems: ScreenContextMenuItem[] = [
    {
        label: 'Back to Kits List',
        icon: 'arrow_back_ios',
        handler: () => this.location.back(),
    },
    {
      label: 'Remove this Collated Set',
      icon: 'delete',
      condition: this.kitAudit.audit?.type == KitAuditType.CollatedSets,
      handler: () => this.cancelCurrentAuditSet(),
    }
  ]

  private scannerSubscriber: Subscription;
  public get audit(): KitAudit { return this.kitAudit.audit!; }
  public get activeKit(): KitAuditSet { return this.kitAudit.audit!.activeKit!; }

  LifeCycleColor = this.MayerFabrics.LifeCycleColor;
  LifeCycleContrastColor = this.MayerFabrics.LifeCycleContrastColor;

  public get sortedItems(): KitAuditItem[] {
    return this.activeKit?.items.sort((a, b) => {
      if(a.checked && !b.checked) return 1;
      if(b.checked && !a.checked) return -1;
      return a.Description.localeCompare(b.Description);
    });
  }

  public ngOnInit(): void {
    // upon entering the kit-audit, if a Memo item was used to start a collated set audit, check it on entry
    if(this.kitAudit.autoCheckInventoryID) {
      this.receiveScannerValue(this.kitAudit.autoCheckInventoryID);
      this.kitAudit.autoCheckInventoryID = undefined;
    }
  }

  public ngOnDestroy(): void {
    this.scannerSubscriber.unsubscribe();
  }

  _scanReceived?: MatSnackBarRef<TextOnlySnackBar>;
  /** handler for the no-input scanner receiver */
  private receiveScannerValue(scanValue: string | Object) {
    this.kitAudit.ScanValueToInventoryID(scanValue)
      .then(inventoryID => {
        this._scanReceived?.dismiss();

        // look for the scanned value in the list of kit item contents
        let searchResult = this.activeKit.items.find(item => item.InventoryID == inventoryID);
        
        // if not found and doing a collated set audit
        if(!searchResult && this.audit.type == KitAuditType.CollatedSets) {
          // check if a rolled item Inventory ID was scanned
          searchResult = this.activeKit.items.find(item => item.InventoryID == inventoryID);
        }

        if(searchResult) {
          this.setAuditCheck(searchResult);
          this.audio.play(Sound.GentleBeep);
          let message = `Received: ${searchResult.InventoryID} - ${searchResult.Description}`;
          this._scanReceived = this.snackbar.open(message, undefined, {duration:2000});
        }
        else {
          this.handleScanValueNotInKit(inventoryID);
        }
      })
      .catch(errorMessage => {
        this.audio.play(Sound.ErrorBeep);
        let notice = new KitAuditNotice_InpersistentMessage(errorMessage);
        this.kitAudit.RegisterNotice(notice);
      })
  }

  /** handler for when user scans a barcode value not in the current kit */
  private handleScanValueNotInKit(value: string) {
    let audit = this.kitAudit.audit!;

    // look for Inventory ID in any other kits
    for(var idx = 0; idx < audit.kits.length; idx++) {
      let foundIn = audit.kits[idx];
      let found = foundIn.items.find(item => item.InventoryID == value);
      if(found) {
        let notice = new KitAuditNotice_ItemInWrongKit(found.InventoryID, found.Description!, this.activeKit, foundIn);
        this.audio.play(Sound.GentleBeep);
        this.kitAudit.RegisterNotice(notice);
        return;
      }
    }

    // look up the Inventory ID in acumatica
    this.kitAudit.LookupItemFromAcumatica(value)
    .then(item => this.handleItemResultFromAcumatica(item))
    .catch(err => {
      console.error(err);
      console.error("Scanned value not found in kit:", value);
    });
  }

  /** handler for when an value is scanned in but is not an item in this or another audit */
  private handleItemResultFromAcumatica(stockItem: StockItem) {
    if(this.audit.type == KitAuditType.CollatedSets) {

      // was the scanned Inventory ID a Rolled good Inventory ID during a Collated Set audit?
      if(stockItem.LotSerialClass == 'R') {
        // "scan in" the Memo inventory ID
        this.receiveScannerValue("M" + stockItem.InventoryID);
        return;
      }

      // was it not a memo item?
      if(stockItem.ItemClassID != '50') {
        this.audio.play(Sound.ErrorBeep);
        let notice = new KitAuditNotice_InpersistentMessage(`${stockItem.InventoryID} - ${stockItem.Description} is not a valid item for Collated Set audits`);
        this.kitAudit.RegisterNotice(notice);        
        return;
      }

      // does it belong to any collated set?
      this.collatedSetInquiry.get(stockItem.InventoryID, {loader:null}).subscribe(result => {
        if(result.Details.length > 0) {
          this.audio.play(Sound.GentleBeep);
          let notice = new KitAuditNotice_InpersistentMessage(`${stockItem.InventoryID} - ${stockItem.Description} belongs in ${result.Details[0].KitDescription}`);
          this.kitAudit.RegisterNotice(notice);
        } else {
          // Not in any Collated Set
          this.audio.play(Sound.GentleBeep);
          let notice = new KitAuditNotice_InpersistentMessage(`${stockItem.InventoryID} - ${stockItem.Description} does not belong to any Collated Sets (Life Cycle: ${stockItem.LifeCycle})`);
          this.kitAudit.RegisterNotice(notice);
        }
      });

    }

    // Binder or Loose Card audits
    else {
      // if it is a Card from a different binder that would've already been identified and notice given
      this.audio.play(Sound.ErrorBeep);
      let notice = new KitAuditNotice_InpersistentMessage(`${stockItem.InventoryID} - ${stockItem.Description} is not a current/active item`);
      this.kitAudit.RegisterNotice(notice);       
    }
    
  }

  /** reverse the checked state of the given item */
  public toggleAuditCheck(item: KitAuditItem) {
    this.setAuditCheck(item, !item.checked);
  }

  /** set the checked state of the given item to the indicated value (true by default) */
  public setAuditCheck(item: KitAuditItem, value: boolean = true) {
    item.checked = value;

    if(item.checked) {
      // any Notices to move item into this binder
      let notice = this.audit.notices.find(notice => notice instanceof KitAuditNotice_ItemInWrongKit && notice.InventoryID == item.InventoryID)
      if(notice) this.kitAudit.audit?.notices.remove(notice);
    }

    this.kitAudit.Persist();

    if(this.activeKit.AllChecked()) this.location.back();
  }

  /** confirms with the user then cancels/removes the current audit set from the audit operation */
  private cancelCurrentAuditSet() {
    this.dialog.open(ConfirmationDialog, {data:{
      icon: 'help',
      message: "Are you sure you want to remove the \"" + this.activeKit.Description + "\" from the current audit?",
      confirmText: 'Yes, Remove it',
      cancelText: 'No, Cancel'
    }}).afterClosed().subscribe(response => {
      if(!response) return;

      this.audit.kits.remove(this.activeKit);
      this.kitAudit.Persist();
      this.location.back();
    })
  }

}
